/**
 * Patient Context Bar Component
 *
 * Main component that displays patient information and context indicators
 * with hover popovers for medications, diagnoses, allergies, and last encounter.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import MarkdownImporter from '@components/Markdown/MarkdownImporter';
import { useSubjectLanguageVariants, useUserState } from '@features/user';
import { Box, Collapse } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { Patient } from '@ts/types';

import { useIndicatorHover } from '../hooks/useIndicatorHover';
import { usePatientContext } from '../hooks/usePatientContext';

import {
  PatientAllergiesPopover,
  PatientDiagnosesEditModal,
  PatientDiagnosesPopover,
  PatientLastEncounterPopover,
  PatientMedicationsEditModal,
  PatientMedicationsPopover,
  PatientTreatmentPlanEditModal,
  PatientTreatmentPlanPopover,
} from './index';
import type { PatientTreatmentPlanEditModalHandle } from './PatientTreatmentPlanEditModal';
import { PatientDisplay } from './PatientDisplay';
import { PatientIndicators } from './PatientIndicators';

export interface PatientContextBarProps {
  /** Patient to display context for */
  patient: Patient | null;
  /** Callback when patient is changed (unselected) */
  onPatientChange?: (patient: Patient | undefined) => void;
  /** Callback when viewing last encounter note */
  onViewLastEncounterNote?: (noteId: string) => void;
  /** Callback when patient name is clicked (e.g., to navigate to patient profile) */
  onPatientClick?: (patientId: string) => void;
  /** Whether patient selection can be edited */
  editable?: boolean;
  /** Optional content rendered right-aligned in the bar (e.g. JotRx CTA) */
  actions?: React.ReactNode;
  /** Source identifier for analytics (where the bar is rendered) */
  source: string;
}

/**
 * Patient Context Bar
 *
 * Displays patient information with context indicators that show
 * detailed information on hover (medications, diagnoses, allergies, last encounter).
 */
export const PatientContextBar: React.FC<PatientContextBarProps> = ({
  patient,
  onPatientChange,
  onViewLastEncounterNote,
  onPatientClick,
  editable = true,
  actions,
  source,
}) => {
  // Fetch patient context data
  const {
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    treatmentPlan,
    loadingState,
    medicationsModuleInfo,
    diagnosesModuleInfo,
  } = usePatientContext(patient?.patient_id || null);

  // Feature flags
  const userState = useUserState();
  const { singular } = useSubjectLanguageVariants();
  const showTreatmentPlan = !!userState?.featureFlags?.treatment_plan;

  // Event tracking
  const { patient: patientTracking, patientContextBar: contextBarTracking } = useEventTracking();

  // Mutations

  // Modal state
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);
  const [treatmentPlanModalOpen, setTreatmentPlanModalOpen] = useState(false);
  const [treatmentPlanImportOpen, setTreatmentPlanImportOpen] = useState(false);
  const treatmentPlanModalRef = useRef<PatientTreatmentPlanEditModalHandle>(null);

  // Hover state for each indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();
  const treatmentPlanHover = useIndicatorHover();

  // Track popover views (fire once per open)
  const medicationsPopoverOpen = medicationsHover.isHovered && !!medicationsHover.anchorEl && !!patient;
  const diagnosesPopoverOpen = diagnosesHover.isHovered && !!diagnosesHover.anchorEl && !!patient;
  const allergiesPopoverOpen = allergiesHover.isHovered && !!allergiesHover.anchorEl && !!patient;
  const lastEncounterPopoverOpen = lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl && !!patient;
  const treatmentPlanPopoverOpen = treatmentPlanHover.isHovered && !!treatmentPlanHover.anchorEl && !!patient && showTreatmentPlan;

  const prevMedsOpen = useRef(false);
  const prevDiagsOpen = useRef(false);
  const prevAllergiesOpen = useRef(false);
  const prevEncounterOpen = useRef(false);
  const prevTreatmentPlanOpen = useRef(false);

  useEffect(() => {
    if (medicationsPopoverOpen && !prevMedsOpen.current && patient?.patient_id) {
      contextBarTracking.popoverViewed({
        patient_id: patient.patient_id,
        indicator_type: 'medications',
        item_count: medications.length,
        source,
      });
    }
    prevMedsOpen.current = medicationsPopoverOpen;
  }, [medicationsPopoverOpen, patient?.patient_id, medications.length, source, contextBarTracking]);

  useEffect(() => {
    if (diagnosesPopoverOpen && !prevDiagsOpen.current && patient?.patient_id) {
      contextBarTracking.popoverViewed({
        patient_id: patient.patient_id,
        indicator_type: 'diagnoses',
        item_count: diagnoses.length,
        source,
      });
    }
    prevDiagsOpen.current = diagnosesPopoverOpen;
  }, [diagnosesPopoverOpen, patient?.patient_id, diagnoses.length, source, contextBarTracking]);

  useEffect(() => {
    if (allergiesPopoverOpen && !prevAllergiesOpen.current && patient?.patient_id) {
      contextBarTracking.popoverViewed({
        patient_id: patient.patient_id,
        indicator_type: 'allergies',
        item_count: allergies.length,
        source,
      });
    }
    prevAllergiesOpen.current = allergiesPopoverOpen;
  }, [allergiesPopoverOpen, patient?.patient_id, allergies.length, source, contextBarTracking]);

  useEffect(() => {
    if (lastEncounterPopoverOpen && !prevEncounterOpen.current && patient?.patient_id) {
      contextBarTracking.popoverViewed({
        patient_id: patient.patient_id,
        indicator_type: 'last_encounter',
        source,
      });
    }
    prevEncounterOpen.current = lastEncounterPopoverOpen;
  }, [lastEncounterPopoverOpen, patient?.patient_id, source, contextBarTracking]);

  useEffect(() => {
    if (treatmentPlanPopoverOpen && !prevTreatmentPlanOpen.current && patient?.patient_id) {
      contextBarTracking.popoverViewed({
        patient_id: patient.patient_id,
        indicator_type: 'treatment_plan',
        source,
      });
    }
    prevTreatmentPlanOpen.current = treatmentPlanPopoverOpen;
  }, [treatmentPlanPopoverOpen, patient?.patient_id, source, contextBarTracking]);

  const handlePatientClose = () => {
    if (onPatientChange) {
      onPatientChange(undefined);
    }
  };

  const handleMedicationsClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setMedicationsModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patient.patient_id,
      data_type: 'medications',
      item_count: medications.length,
      source,
    });
  }, [patient?.patient_id, medications.length, source, contextBarTracking]);

  const handleDiagnosesClick = useCallback(() => {
    if (!patient?.patient_id) return;
    setDiagnosesModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patient.patient_id,
      data_type: 'diagnoses',
      item_count: diagnoses.length,
      source,
    });
  }, [patient?.patient_id, diagnoses.length, source, contextBarTracking]);

  const handleMedicationsUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['medications'],
    });
  }, [patient?.patient_id, patientTracking]);

  const handleDiagnosesUpdated = useCallback(() => {
    if (!patient?.patient_id) return;
    patientTracking.updated(patient.patient_id, {
      source: 'context_bar',
      fields_updated: ['diagnoses'],
    });
  }, [patient?.patient_id, patientTracking]);

  const handleViewNote = useCallback(
    (noteId: string) => {
      if (patient?.patient_id) {
        contextBarTracking.viewNoteClicked({
          patient_id: patient.patient_id,
          note_id: noteId,
          source,
        });
      }
      onViewLastEncounterNote?.(noteId);
    },
    [patient?.patient_id, source, contextBarTracking, onViewLastEncounterNote],
  );

  const handleTreatmentPlanEdit = useCallback(() => {
    if (!patient?.patient_id) return;
    setTreatmentPlanModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patient.patient_id,
      data_type: 'treatment_plan',
      item_count: treatmentPlan ? 1 : 0,
      source,
    });
  }, [patient?.patient_id, treatmentPlan, source, contextBarTracking]);

  const handleMedicationsEditFromPopover = useCallback(() => {
    if (!patient?.patient_id) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patient.patient_id,
      data_type: 'medications',
      source,
    });
    handleMedicationsClick();
  }, [patient?.patient_id, source, contextBarTracking, handleMedicationsClick]);

  const handleDiagnosesEditFromPopover = useCallback(() => {
    if (!patient?.patient_id) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patient.patient_id,
      data_type: 'diagnoses',
      source,
    });
    handleDiagnosesClick();
  }, [patient?.patient_id, source, contextBarTracking, handleDiagnosesClick]);

  const handleTreatmentPlanEditFromPopover = useCallback(() => {
    if (!patient?.patient_id) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patient.patient_id,
      data_type: 'treatment_plan',
      source,
    });
    handleTreatmentPlanEdit();
  }, [patient?.patient_id, source, contextBarTracking, handleTreatmentPlanEdit]);

  const handleTreatmentPlanImport = useCallback(() => {
    if (patient?.patient_id) {
      contextBarTracking.importClicked({
        patient_id: patient.patient_id,
        data_type: 'treatment_plan',
        source,
      });
    }
    setTreatmentPlanImportOpen(true);
  }, [patient?.patient_id, source, contextBarTracking]);

  const handleImportSuccess = useCallback(
    async (markdown: string) => {
      if (!patient?.patient_id) return;
      try {
        // Load imported markdown directly into the editor — autosave will handle persisting
        await treatmentPlanModalRef.current?.loadMarkdown(markdown);
        setTreatmentPlanImportOpen(false);
        contextBarTracking.importCompleted({
          patient_id: patient.patient_id,
          data_type: 'treatment_plan',
          source,
        });
      } catch (error) {
        contextBarTracking.importFailed({
          patient_id: patient.patient_id,
          data_type: 'treatment_plan',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
      }
    },
    [patient?.patient_id, source, contextBarTracking],
  );

  return (
    <>
      <Collapse in={!!patient || !!actions}>
        <Box
          sx={{
            backgroundColor: '#FCFCFC',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: 1400,
              mx: 'auto',
            }}
          >
            {patient && (
              <>
                <PatientDisplay
                  patient={patient}
                  onClose={editable ? handlePatientClose : undefined}
                  onPatientClick={onPatientClick}
                  source={source}
                />
                <PatientIndicators
                  medicationsHover={medicationsHover}
                  diagnosesHover={diagnosesHover}
                  allergiesHover={allergiesHover}
                  lastEncounterHover={lastEncounterHover}
                  treatmentPlanHover={showTreatmentPlan ? treatmentPlanHover : undefined}
                  onMedicationsClick={handleMedicationsClick}
                  onDiagnosesClick={handleDiagnosesClick}
                />
              </>
            )}
            {actions && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {actions}
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Medications Popover */}
      <PatientMedicationsPopover
        medications={medications}
        anchorEl={medicationsHover.anchorEl}
        open={medicationsHover.isHovered && !!medicationsHover.anchorEl && !!patient}
        onClose={() => {
          medicationsHover.handlePopoverLeave();
        }}
        onMouseEnter={medicationsHover.handlePopoverEnter}
        onMouseLeave={medicationsHover.handlePopoverLeave}
        isLoading={loadingState.medications}
        onEdit={handleMedicationsEditFromPopover}
      />

      {/* Diagnoses Popover */}
      <PatientDiagnosesPopover
        diagnoses={diagnoses}
        anchorEl={diagnosesHover.anchorEl}
        open={diagnosesHover.isHovered && !!diagnosesHover.anchorEl && !!patient}
        onClose={() => {
          diagnosesHover.handlePopoverLeave();
        }}
        onMouseEnter={diagnosesHover.handlePopoverEnter}
        onMouseLeave={diagnosesHover.handlePopoverLeave}
        isLoading={loadingState.diagnoses}
        onEdit={handleDiagnosesEditFromPopover}
      />

      {/* Allergies Popover */}
      <PatientAllergiesPopover
        allergies={allergies}
        anchorEl={allergiesHover.anchorEl}
        open={allergiesHover.isHovered && !!allergiesHover.anchorEl && !!patient}
        onClose={() => {
          allergiesHover.handlePopoverLeave();
        }}
        onMouseEnter={allergiesHover.handlePopoverEnter}
        onMouseLeave={allergiesHover.handlePopoverLeave}
        isLoading={loadingState.allergies}
      />

      {/* Last Encounter Popover */}
      <PatientLastEncounterPopover
        data={lastEncounter}
        anchorEl={lastEncounterHover.anchorEl}
        open={lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl && !!patient}
        onClose={() => {
          lastEncounterHover.handlePopoverLeave();
        }}
        onMouseEnter={lastEncounterHover.handlePopoverEnter}
        onMouseLeave={lastEncounterHover.handlePopoverLeave}
        isLoading={loadingState.lastEncounter}
        onViewNote={handleViewNote}
      />

      {/* Medications Edit Modal */}
      {patient?.patient_id && medicationsModuleInfo && (
        <PatientMedicationsEditModal
          open={medicationsModalOpen}
          onClose={() => setMedicationsModalOpen(false)}
          patientId={patient.patient_id}
          medications={medications}
          moduleInfo={medicationsModuleInfo}
          onMedicationsUpdated={handleMedicationsUpdated}
          source={source}
        />
      )}

      {/* Diagnoses Edit Modal */}
      {patient?.patient_id && diagnosesModuleInfo && (
        <PatientDiagnosesEditModal
          open={diagnosesModalOpen}
          onClose={() => setDiagnosesModalOpen(false)}
          patientId={patient.patient_id}
          diagnoses={diagnoses}
          moduleInfo={diagnosesModuleInfo}
          onDiagnosesUpdated={handleDiagnosesUpdated}
          source={source}
        />
      )}

      {/* Treatment Plan Popover */}
      {showTreatmentPlan && (
        <PatientTreatmentPlanPopover
          data={treatmentPlan}
          anchorEl={treatmentPlanHover.anchorEl}
          open={treatmentPlanPopoverOpen}
          onClose={() => {
            treatmentPlanHover.handlePopoverLeave();
          }}
          onMouseEnter={treatmentPlanHover.handlePopoverEnter}
          onMouseLeave={treatmentPlanHover.handlePopoverLeave}
          isLoading={loadingState.treatmentPlan}
          onEdit={handleTreatmentPlanEditFromPopover}
        />
      )}

      {/* Treatment Plan Edit Modal */}
      {patient?.patient_id && showTreatmentPlan && (
        <PatientTreatmentPlanEditModal
          ref={treatmentPlanModalRef}
          open={treatmentPlanModalOpen}
          onClose={() => setTreatmentPlanModalOpen(false)}
          patientId={patient.patient_id}
          treatmentPlan={treatmentPlan}
          source={source}
          onImport={handleTreatmentPlanImport}
        />
      )}

      {/* Treatment Plan Import */}
      {patient?.patient_id && showTreatmentPlan && (
        <MarkdownImporter
          isOpen={treatmentPlanImportOpen}
          onClose={() => setTreatmentPlanImportOpen(false)}
          onImportSuccess={handleImportSuccess}
          endpoint="/treatment_plan"
          contentFieldName="text"
          additionalParams={{ patient_id: patient.patient_id }}
          title="Import Treatment Plan"
          description="Paste text or drag a file to import a treatment plan. Supported file types: .pdf, .docx, .jpg, .jpeg, .png"
          acceptedFileTypes=".pdf,.docx,.jpg,.jpeg,.png"
          warningText={
            treatmentPlan && treatmentPlan.markdown.trim().length > 0
              ? `Note: This import will overwrite the existing treatment plan for this ${singular}.`
              : undefined
          }
        />
      )}
    </>
  );
};
